// client/src/coms/components/sidebar_parts/SidebarFooter.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SoundboardPanel, { shortcutFromEvent } from '../SoundboardPanel';
import ComsModal from '../ComsModal';
import ServerSoundboardManager from '../serversettings_parts/ServerSoundboardManager';
import { listLocalSounds, updateLocalSound } from '../../soundboard/soundStore';
import { subscribeSoundboardActivity, triggerSound, unlockAudio } from '../../soundboard/soundboardEngine';
import AccountSwitcher from '../../accounts/AccountSwitcher';
import { MyStatusDot } from '../../presence/QuickStatus';

const LS_VOICE_MUTED = 'vox:voiceMutedPref';
const LS_VOICE_DEAFENED = 'vox:voiceDeafenedPref';
const MIC_ICON_SRC = '/mic.png';
const SCREEN_ICON_SRC = '/screen.png';

function readBoolLS(key, fallback = false) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return fallback;
    const raw = window.localStorage.getItem(key);
    if (raw === '1' || raw === 'true') return true;
    if (raw === '0' || raw === 'false') return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function writeBoolLS(key, value) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {}
}

function SidebarAvatar({ avatarUrl, name, size = 28, fontSize = 13 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        background: 'var(--vxs-1, #111214)',
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.8 * var(--vx-ov, 1)))',
        fontSize,
        flexShrink: 0,
      }}
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={(e) => {
            try {
              e.currentTarget.style.display = 'none';
            } catch {}
          }}
        />
      ) : (
        <span>{String(name || '?').trim().charAt(0).toUpperCase() || '?'}</span>
      )}
    </div>
  );
}

function WhiteIconImage({ src, size = 18 }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        display: 'block',
        objectFit: 'contain',
        filter: 'brightness(0) invert(1)',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
      draggable={false}
    />
  );
}

function VoiceMicIcon({ size = 18 }) {
  return <WhiteIconImage src={MIC_ICON_SRC} size={size} />;
}

function VoiceScreenIcon({ size = 18 }) {
  return <WhiteIconImage src={SCREEN_ICON_SRC} size={size} />;
}

// A soundboard: a speaker with sound waves.
export function SoundboardIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ display: 'block', pointerEvents: 'none' }}>
      <path d="M4 9.5h3l4-3.5v12l-4-3.5H4z" fill="currentColor" />
      <path d="M14.5 8.5a5 5 0 0 1 0 7M17 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function VoiceEmojiIcon({ emoji, size = 19 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block',
        fontSize: size,
        lineHeight: 1,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {emoji}
    </span>
  );
}

function getVoiceButtonStyle(isAlert) {
  if (!isAlert) return null;
  return {
    background: 'linear-gradient(180deg, #ff5a5a 0%, #d81f3a 100%)',
    color: '#fff',
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.2 * var(--vx-ov, 1)))',
    boxShadow: '0 0 0 1px rgba(255,80,80,0.35), 0 0 14px rgba(255,50,50,0.45)',
    transform: 'scale(1.06)',
  };
}

function GamesFooterButton({ className, onOpenGames, gamesActive, activeGame }) {
  if (typeof onOpenGames !== 'function') return null;
  const label = activeGame ? `Return to ${activeGame.name}` : 'Activities';
  return (
    <button
      type="button"
      data-testid="sidebar-games"
      data-vx-activities-button=""
      className={`${className} ${gamesActive ? 'active' : ''}`}
      title={label}
      aria-label={label}
      onClick={onOpenGames}
      style={{ position: 'relative' }}
    >
      <VoiceEmojiIcon emoji="🚀" size={17} />
      {activeGame ? (
        <span
          data-testid="sidebar-games-live"
          aria-hidden="true"
          style={{ position: 'absolute', top: 2, right: 2, width: 8, height: 8, borderRadius: 999, background: '#23a55a' }}
        />
      ) : null}
    </button>
  );
}

export function SidebarFooter({
  showVoiceBar,
  myAvatarUrl,
  displayName,
  voiceSession,
  activeVoiceName,
  voiceVideoErr,
  onToggleVoiceMute,
  onToggleVoiceDeafen,
  voiceShowVideo,
  onToggleVoiceVideo,
  isOwner,
  openServerSettings,
  onOpenTab,
  onLeaveVoiceChannel,
  onOpenGames,
  gamesActive = false,
  activeGame = null,
  serverKey = '',
}) {
  const [rememberedMuted, setRememberedMuted] = useState(false);
  const [rememberedDeafened, setRememberedDeafened] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountBtnRef = useRef(null);
  const accountMenu = (
    <AccountSwitcher
      open={accountOpen}
      anchorRef={accountBtnRef}
      onClose={() => setAccountOpen(false)}
      currentUser={{ username: displayName }}
      voiceSession={voiceSession}
      onOpenSettings={() => {
        try {
          window.__voxPendingSettingsTab = 'Account';
          window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'Account' } }));
        } catch {}
        if (typeof onOpenTab === 'function') onOpenTab('settings');
      }}
      onOpenStatusSettings={() => {
        try {
          window.__voxPendingSettingsTab = 'privacy';
          window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'privacy' } }));
        } catch {}
        setAccountOpen(false);
        if (typeof onOpenTab === 'function') onOpenTab('settings');
      }}
    />
  );
  const accountButtonProps = {
    ref: accountBtnRef,
    type: 'button',
    'aria-haspopup': 'dialog',
    'aria-expanded': accountOpen,
    'aria-label': `Account menu${displayName ? `, signed in as ${displayName}` : ''}`,
    title: 'Switch account',
    'data-testid': 'account-menu-button',
    onClick: () => setAccountOpen((v) => !v),
  };

  useEffect(() => {
    setRememberedMuted(readBoolLS(LS_VOICE_MUTED, false));
    setRememberedDeafened(readBoolLS(LS_VOICE_DEAFENED, false));
  }, []);

  const voiceStatusValue = String((voiceSession && voiceSession.status) || '').trim().toLowerCase();
  const [soundboardOpen, setSoundboardOpen] = useState(false);
  const [serverSoundsOpen, setServerSoundsOpen] = useState(false);
  const [soundActivity, setSoundActivity] = useState(null);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const voiceRoomName = voiceStatusValue === 'connected' ? String((voiceSession && voiceSession.roomName) || '') : '';
  const soundServerKey = String((voiceRoomName && voiceSession && voiceSession.serverId) || serverKey || '');

  // "X played 🔔 Boing" indicator, and a visible fix when the browser blocks sound playback.
  useEffect(() => {
    let timer = null;
    const off = subscribeSoundboardActivity((a) => {
      if (a.type === 'blocked') setSoundBlocked(true);
      if (a.type === 'audio_state' && a.state === 'running') setSoundBlocked(false);
      if (a.type !== 'play') return;
      setSoundActivity(a);
      clearTimeout(timer);
      timer = setTimeout(() => setSoundActivity(null), 3500);
    });
    return () => {
      off();
      clearTimeout(timer);
    };
  }, []);

  // Keyboard shortcuts for device-local sounds while in a voice channel (never while typing).
  useEffect(() => {
    if (!voiceRoomName) return undefined;
    const onKey = async (e) => {
      const el = e.target;
      const tag = String((el && el.tagName) || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (el && el.isContentEditable)) return;
      const sc = shortcutFromEvent(e);
      if (!sc) return;
      const sounds = await listLocalSounds().catch(() => []);
      const hit = sounds.find((s) => s.shortcut === sc);
      if (!hit) return;
      e.preventDefault();
      triggerSound(voiceRoomName, hit, { onServerHash: (h) => updateLocalSound(hit.id, { serverHash: h }).catch(() => {}) }).catch(() => {});
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [voiceRoomName]);

  const soundboardParticipants = useMemo(() => {
    const list = Array.isArray(voiceSession && voiceSession.participants) ? voiceSession.participants : [];
    return list
      .map((p) => ({ userId: p && (p.userId != null ? p.userId : String(p.identity || '').replace(/\D+/g, '')), name: p && (p.name || p.displayName) }))
      .filter((p) => p.userId && String(p.userId) !== String(voiceSession && voiceSession.myUserId));
  }, [voiceSession]);
  const soundboardButton = (className) => (
    <button
      type="button"
      className={`${className} ${soundboardOpen ? 'active' : ''}`}
      title="Soundboard"
      aria-label="Open soundboard"
      aria-expanded={soundboardOpen}
      data-testid="sidebar-soundboard"
      onClick={() => setSoundboardOpen((v) => !v)}
    >
      <SoundboardIcon size={17} />
    </button>
  );

  const soundboardUi = (
    <>
      <SoundboardPanel
        open={soundboardOpen}
        onClose={() => setSoundboardOpen(false)}
        roomName={voiceRoomName}
        serverKey={soundServerKey}
        participants={soundboardParticipants}
        onManageServer={() => {
          setSoundboardOpen(false);
          setServerSoundsOpen(true);
        }}
        onOpenSettings={() => {
          setSoundboardOpen(false);
          try {
            window.__voxPendingSettingsTab = 'Soundboard';
            window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'Soundboard' } }));
          } catch {}
          if (onOpenTab) onOpenTab('settings');
        }}
      />
      {serverSoundsOpen && soundServerKey
        ? createPortal(
            <ComsModal title="Server soundboard" onClose={() => setServerSoundsOpen(false)} width="760px">
              <ServerSoundboardManager serverKey={soundServerKey} />
            </ComsModal>,
            document.body
          )
        : null}
    </>
  );

  const hasVoiceSessionTarget = !!(
    voiceSession &&
    String(voiceSession.serverId || '').trim() &&
    String(voiceSession.channelId || '').trim()
  );
  const isConnected = !!(
    voiceSession &&
    (
      voiceStatusValue === 'joining' ||
      voiceStatusValue === 'connecting' ||
      voiceStatusValue === 'connected' ||
      voiceStatusValue === 'reconnecting' ||
      hasVoiceSessionTarget
    )
  );

  const effectiveMuted = isConnected ? !!voiceSession.muted : rememberedMuted;
  const effectiveDeafened = isConnected ? !!voiceSession.deafened : rememberedDeafened;

  const handleToggleMute = () => {
    const next = !effectiveMuted;
    setRememberedMuted(next);
    writeBoolLS(LS_VOICE_MUTED, next);
    if (onToggleVoiceMute) onToggleVoiceMute();
  };

  const handleToggleDeafen = () => {
    const next = !effectiveDeafened;
    setRememberedDeafened(next);
    writeBoolLS(LS_VOICE_DEAFENED, next);
    if (onToggleVoiceDeafen) onToggleVoiceDeafen();
  };

  const voiceButtons = useMemo(() => {
    return [
      {
        key: 'mute',
        title: effectiveMuted ? 'Mic muted' : 'Mute mic',
        onClick: handleToggleMute,
        className: `coms-voicebar-btn ${effectiveMuted ? 'active' : ''}`,
        style: getVoiceButtonStyle(effectiveMuted),
        icon: <VoiceMicIcon />,
      },
      {
        key: 'deafen',
        title: effectiveDeafened ? 'Speakers muted' : 'Mute speakers',
        onClick: handleToggleDeafen,
        className: `coms-voicebar-btn ${effectiveDeafened ? 'active' : ''}`,
        style: getVoiceButtonStyle(effectiveDeafened),
        icon: <VoiceEmojiIcon emoji="🎧" size={19} />,
      },
      {
        key: 'video',
        title: voiceShowVideo ? 'Hide video' : 'Show video',
        onClick: () => onToggleVoiceVideo && onToggleVoiceVideo(),
        className: `coms-voicebar-btn ${voiceShowVideo ? 'active' : ''}`,
        style: getVoiceButtonStyle(!!voiceShowVideo),
        icon: <VoiceScreenIcon />,
      },
      {
        key: 'disconnect',
        title: 'Disconnect',
        onClick: () => onLeaveVoiceChannel && onLeaveVoiceChannel(),
        className: 'coms-voicebar-btn danger',
        icon: <VoiceEmojiIcon emoji="⛔" size={18} />,
      },
    ];
  }, [effectiveMuted, effectiveDeafened, voiceShowVideo, onToggleVoiceVideo, onLeaveVoiceChannel]);

  if (showVoiceBar) {
    return (
      <div className="coms-voicebar">
        <div className="coms-voicebar-left-wrap">
          <div className="coms-voicebar-left" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <button {...accountButtonProps} className="vx-user-trigger" style={{ padding: 2, margin: -2, position: 'relative' }}>
              <SidebarAvatar avatarUrl={myAvatarUrl} name={displayName} />
              <MyStatusDot />
            </button>
            {accountMenu}

            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div
                className="coms-voicebar-title"
                style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
                title={displayName}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {displayName}
                </span>
                <span style={{ opacity: 0.65, flexShrink: 0 }}>
                  {voiceStatusValue === 'joining' || voiceStatusValue === 'connecting'
                    ? 'Connecting…'
                    : voiceStatusValue === 'reconnecting' || (voiceSession && voiceSession.connection === 'reconnecting')
                      ? 'Reconnecting…'
                      : voiceStatusValue === 'error'
                        ? 'Not connected'
                        : 'In Voice'}
                </span>
              </div>

              <div className="coms-voicebar-sub">{activeVoiceName}</div>

              {soundBlocked && voiceRoomName ? (
                <button
                  type="button"
                  className="coms-mini-btn tiny"
                  style={{ marginTop: 3, borderColor: 'rgba(245,158,11,0.6)', color: '#fde68a' }}
                  onClick={() => unlockAudio().then((s) => setSoundBlocked(s !== 'running'))}
                  data-testid="sound-unlock"
                >
                  🔇 Sounds blocked by the browser: enable
                </button>
              ) : soundActivity ? (
                <div role="status" style={{ marginTop: 2, fontSize: 11, opacity: soundActivity.audible ? 0.9 : 0.5, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {soundActivity.emoji || '🔊'} {soundActivity.name || 'a sound'}
                  {soundActivity.sourceServer ? ` · ${soundActivity.sourceServer}` : ''}
                  {soundActivity.audible ? '' : ' (muted)'}
                </div>
              ) : null}

              {soundboardUi}

              {voiceSession && (voiceSession.error || voiceSession.notice) ? (
                <div
                  role={voiceSession.error ? 'alert' : 'status'}
                  style={{
                    marginTop: 2,
                    fontSize: 11,
                    color: voiceSession.error ? '#ff6b6b' : 'rgba(255,200,120,0.95)',
                    fontWeight: 700,
                    maxWidth: 220,
                    whiteSpace: 'normal',
                    lineHeight: '14px',
                  }}
                >
                  {voiceSession.error || voiceSession.notice}
                </div>
              ) : null}

              {voiceVideoErr ? (
                <div
                  style={{
                    marginTop: 2,
                    fontSize: 11,
                    color: '#ff6b6b',
                    fontWeight: 800,
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={voiceVideoErr}
                >
                  {voiceVideoErr}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="coms-voicebar-right">
          <div className="coms-voicebar-actions-top">
            <GamesFooterButton className="coms-voicebar-btn" onOpenGames={onOpenGames} gamesActive={gamesActive} activeGame={activeGame} />
            {soundboardButton('coms-voicebar-btn')}
            {isOwner && (
              <button
                type="button"
                className="coms-voicebar-btn"
                title="Server settings"
                onClick={openServerSettings}
              >
                <VoiceEmojiIcon emoji="🛠" size={17} />
              </button>
            )}

            <button
              type="button"
              className="coms-voicebar-btn"
              title="Settings"
              onClick={() => onOpenTab && onOpenTab('settings')}
            >
              <VoiceEmojiIcon emoji="⚙" size={17} />
            </button>
          </div>

          <div className="coms-voicebar-actions-bottom">
            {voiceButtons.map((btn) => (
              <button
                key={btn.key}
                type="button"
                className={btn.className}
                title={btn.title}
                onClick={btn.onClick}
                style={btn.style}
              >
                {btn.icon}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coms-userbar" title={displayName ? 'Signed in' : 'Not signed in'}>
      <button {...accountButtonProps} className="coms-userbar-left vx-user-trigger">
        <span className="coms-userbar-avatar" style={{ position: 'relative' }}>
          <SidebarAvatar avatarUrl={myAvatarUrl} name={displayName} />
          <MyStatusDot />
        </span>
        <span className="coms-userbar-name">{displayName}</span>
      </button>
      {accountMenu}

      <div className="coms-userbar-actions">
        <GamesFooterButton className="coms-usericon" onOpenGames={onOpenGames} gamesActive={gamesActive} activeGame={activeGame} />
        {soundboardButton('coms-usericon')}
        {soundboardUi}
        {isOwner && (
          <button
            type="button"
            className="coms-usericon"
            title="Server settings"
            onClick={openServerSettings}
          >
            <VoiceEmojiIcon emoji="🛠" size={17} />
          </button>
        )}

        <button
          type="button"
          className="coms-usericon"
          title="Settings"
          onClick={() => onOpenTab('settings')}
        >
          <VoiceEmojiIcon emoji="⚙" size={17} />
        </button>
      </div>
    </div>
  );
}